import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, Pressable, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Badge from '@/components/Typography/Badge';
import Icon from '@/components/Icon';
import { useTranslation } from '@/hooks/useTranslation';
import useRequireAuth from '@/hooks/useRequireAuth';
import { useAuthStore, useCoworkerStore, useModalStore } from '@/store';
import { blockIfWorkModeDisabled } from '@/lib/workModeAvailability';

const MODES = [
  {
    id: 'chat',
    labelKey: 'chatInput.coworkerMode.chat',
    hintKey: 'chatInput.coworkerMode.chatHint',
    detailKey: 'chatInput.coworkerMode.chatTooltip',
    examplesKey: 'chatInput.coworkerMode.chatExamples',
  },
  {
    id: 'work',
    labelKey: 'chatInput.coworkerMode.coworker',
    hintKey: 'chatInput.coworkerMode.workHint',
    detailKey: 'chatInput.coworkerMode.workTooltip',
    examplesKey: 'chatInput.coworkerMode.workExamples',
    badgeKey: 'chatInput.coworkerMode.beta',
  },
];

function pillMetrics(compact, placement) {
  if (placement === 'header') {
    return { minWidth: 118, padH: 28, padV: 8, fontSize: 17, trackPad: 4 };
  }
  if (compact) {
    return { minWidth: 118, padH: 28, padV: 12, fontSize: 17, trackPad: 5 };
  }
  return { minWidth: 112, padH: 26, padV: 7, fontSize: 16, trackPad: 4 };
}

export function useComposerModeChange(source = 'composer_coworker_mode') {
  const { t } = useTranslation();
  const requireAuth = useRequireAuth();
  const setComposerMode = useCoworkerStore((s) => s.setComposerMode);
  const isPlus = useAuthStore((s) => s.user?.flags?.isPlus === true);
  const { openModal } = useModalStore((s) => s.actions);
  return useCallback((mode) => {
    if (mode === 'work') {
      if (blockIfWorkModeDisabled()) return;
      const gated = requireAuth(() => true, {
        source,
        title: t('chatInput.coworkerMode.authTitle'),
        subtitle: t('chatInput.coworkerMode.authSubtitle'),
        icon: 'robot',
      });
      if (gated !== true) return;
      if (!isPlus) {
        openModal('PricingModal', {
          title: t('work.plusRequired.title'),
          subtitle: t('work.plusRequired.subtitle'),
        });
        return;
      }
      setComposerMode('coworker');
      return;
    }
    setComposerMode('chat');
  }, [isPlus, openModal, requireAuth, setComposerMode, source, t]);
}

/**
 * Chat / Work pill. Lives on new-chat composers (home, collections),
 * not inside an open thread. The parent switches composerMode so send
 * launches a Work thread.
 */
function ComposerModeSwitch({
  value = 'chat',
  onChange,
  compact = false,
  pillsOnly = false,
  hidePills = false,
  placement,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [helpOpen, setHelpOpen] = useState(false);
  const rootRef = useRef(null);
  const metrics = pillMetrics(compact, placement);
  const showPills = !hidePills;
  const showCaption = !pillsOnly;

  const handlePress = useCallback((mode) => {
    if (mode === value) return;
    onChange?.(mode);
  }, [onChange, value]);

  const toggleHelp = useCallback(() => {
    setHelpOpen((open) => !open);
  }, []);

  useEffect(() => {
    if (!helpOpen || Platform.OS !== 'web') return undefined;

    const onPointerDown = (event) => {
      const node = rootRef.current;
      if (typeof node?.contains === 'function' && !node.contains(event.target)) {
        setHelpOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setHelpOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [helpOpen]);

  const caption = showCaption ? (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        marginTop: showPills ? (compact ? 8 : 10) : 0,
      }}
    >
      <Text
        size="n"
        color="secondary"
        weight="medium"
      >
        {t('chatInput.coworkerMode.chooseTitle')}
      </Text>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('chatInput.coworkerMode.helpA11y')}
        accessibilityState={{ expanded: helpOpen }}
        hitSlop={8}
        onPress={toggleHelp}
        style={({ hovered, pressed }) => ({
          width: 24,
          height: 24,
          borderRadius: 12,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: helpOpen || hovered || pressed
            ? colors.solid_color_tertiary
            : 'transparent',
          ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
        })}
      >
        <Icon
          name="circle-question"
          size={15}
          color={helpOpen ? colors.text_color_primary : colors.text_color_secondary}
        />
      </Pressable>
    </View>
  ) : null;

  return (
    <View ref={rootRef} style={{ alignItems: 'center' }}>
      {showPills ? (
        <View
          accessibilityLabel={t('chatInput.coworkerMode.chooseTitle')}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            padding: metrics.trackPad,
            borderRadius: 999,
            backgroundColor: colors.solid_color_tertiary,
            flexShrink: 0,
          }}
        >
          {MODES.map((item) => {
            const selected = value === item.id;
            const label = t(item.labelKey);
            // App store review does not allow "beta" labelling in the native
            // apps, so the tag is web-only.
            const badge = Platform.OS === 'web' && item.badgeKey ? t(item.badgeKey) : null;
            return (
              <Pressable
                key={item.id}
                accessibilityRole="button"
                accessibilityState={{ selected }}
                accessibilityLabel={badge ? `${label} (${badge})` : label}
                accessibilityHint={t(item.hintKey)}
                onPress={() => handlePress(item.id)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 5,
                  minWidth: metrics.minWidth,
                  paddingHorizontal: metrics.padH,
                  paddingVertical: metrics.padV,
                  borderRadius: 999,
                  backgroundColor: selected ? colors.solid_color_primary : 'transparent',
                  ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
                }}
              >
                <Text
                  weight="semibold"
                  style={{
                    color: selected ? colors.text_color_primary : colors.text_color_secondary,
                    fontSize: metrics.fontSize,
                  }}
                >
                  {label}
                </Text>
                {badge ? (
                  <Badge
                    text={badge}
                    isSmall
                    textColor={selected ? 'secondary' : 'tertiary'}
                    backgroundColor={selected ? colors.solid_color_tertiary : colors.solid_color_secondary}
                    style={{
                      borderRadius: 999,
                      paddingHorizontal: 6,
                      marginRight: -6,
                    }}
                  />
                ) : null}
              </Pressable>
            );
          })}
        </View>
      ) : null}
      {caption}
      {showCaption && helpOpen ? (
        <View
          style={{
            marginTop: compact ? 8 : 12,
            width: '100%',
            maxWidth: compact ? 300 : 360,
            paddingVertical: compact ? 10 : 12,
            paddingHorizontal: compact ? 12 : 14,
            borderRadius: 12,
            backgroundColor: colors.solid_color_secondary,
            borderWidth: 1,
            borderColor: colors.border_color_secondary,
            gap: compact ? 10 : 14,
          }}
        >
          {MODES.map((item) => {
            const selected = value === item.id;
            const examples = t(item.examplesKey);
            return (
              <View key={item.id} style={{ gap: compact ? 2 : 4 }}>
                <Text
                  size="s"
                  weight="semibold"
                  color={selected ? 'primary' : 'secondary'}
                >
                  {t(item.labelKey)}
                </Text>
                <Text
                  size="s"
                  color="secondary"
                  style={{ lineHeight: compact ? 16 : 18 }}
                >
                  {t(item.detailKey)}
                </Text>
                {Array.isArray(examples) && examples.length > 0 ? (
                  <View style={{ gap: 2, marginTop: 2 }}>
                    {examples.map((example) => (
                      <View
                        key={example}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'flex-start',
                          gap: 7,
                        }}
                      >
                        <Text size="xs" color="tertiary" style={{ lineHeight: 16 }}>
                          •
                        </Text>
                        <Text
                          size="xs"
                          color="tertiary"
                          style={{ flex: 1, lineHeight: 16 }}
                        >
                          {example}
                        </Text>
                      </View>
                    ))}
                  </View>
                ) : null}
              </View>
            );
          })}
        </View>
      ) : null}
    </View>
  );
}

export default React.memo(ComposerModeSwitch);
